import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Bell, Send, Users, Store, Settings, Mail, MessageSquare, 
  Smartphone, Clock, CheckCircle2, AlertCircle, Loader2,
  Filter, Search, ChevronRight, History, BellRing, Inbox,
  CheckCheck, Eye, ChevronDown, CreditCard, ShoppingCart, Megaphone
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { isPushSupported, subscribeToPush, unsubscribeFromPush, getPushStatus } from '../../utils/pushNotifications';

const AdminNotifications = () => {
  const { apiCall, token } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // All Notifications feed state
  const [feedNotifications, setFeedNotifications] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedLoadingMore, setFeedLoadingMore] = useState(false);
  const [feedSkip, setFeedSkip] = useState(0);
  const [feedHasMore, setFeedHasMore] = useState(true);
  const [feedUnreadOnly, setFeedUnreadOnly] = useState(false);
  const [feedUnreadCount, setFeedUnreadCount] = useState(0);
  const FEED_PAGE_SIZE = 20;

  // Push notification subscription state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  // Notification Settings State
  const [settings, setSettings] = useState({
    // Admin alerts
    new_vendor_applications: true,
    listing_approvals: true,
    high_value_orders: true,
    system_alerts: true,
    compliance_requests: true,
    support_tickets: true,
    // Delivery preferences
    email_enabled: true,
    sms_enabled: false,
    push_enabled: true,
    // Digest settings
    daily_digest: true,
    digest_time: '09:00'
  });

  // Push Notification State
  const [pushNotification, setPushNotification] = useState({
    title: '',
    message: '',
    target_audience: 'all_vendors',
    vendor_ids: [],
    priority: 'normal',
    send_email: true,
    send_sms: false,
    send_push: true,
    schedule_type: 'now',
    scheduled_time: ''
  });
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorResults, setVendorResults] = useState([]);
  const [vendorSearching, setVendorSearching] = useState(false);

  // Notification History
  const [history, setHistory] = useState([]);

  // Fetch notification history from backend
  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const data = await apiCall('/api/admin/sent-notifications');
      if (data?.data) {
        setHistory(data.data);
      }
    } catch (error) {
      console.error('Error fetching notification history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [apiCall]);

  // Check push subscription status
  const checkPushStatus = useCallback(async () => {
    if (!token) return;
    setPushSupported(isPushSupported());
    if (isPushSupported()) {
      const status = await getPushStatus(token);
      setPushSubscribed(status.isSubscribed);
    }
  }, [token]);

  // Toggle push subscription
  const handleTogglePush = async () => {
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        const result = await unsubscribeFromPush(token);
        if (result.success) {
          setPushSubscribed(false);
          toast.success('Unsubscribed from push notifications');
        } else {
          toast.error(result.message);
        }
      } else {
        // Register service worker first
        if ('serviceWorker' in navigator) {
          await navigator.serviceWorker.register('/push-sw.js');
        }
        const result = await subscribeToPush(token);
        if (result.success) {
          setPushSubscribed(true);
          toast.success('Subscribed to push notifications');
        } else {
          toast.error(result.message);
        }
      }
    } catch (error) {
      toast.error('Failed to toggle push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  useEffect(() => {
    checkPushStatus();
    fetchHistory();
  }, [checkPushStatus, fetchHistory]);

  // Fetch notification feed
  const fetchFeed = useCallback(async (skip = 0, append = false) => {
    try {
      if (skip === 0) setFeedLoading(true);
      else setFeedLoadingMore(true);
      
      const params = new URLSearchParams({ skip: String(skip), limit: String(FEED_PAGE_SIZE) });
      if (feedUnreadOnly) params.set('unread_only', 'true');
      
      const [feedRes, countRes] = await Promise.all([
        apiCall(`/api/admin/notifications/feed?${params}`),
        skip === 0 ? apiCall('/api/admin/notifications/unread-count') : Promise.resolve(null)
      ]);
      
      const items = feedRes?.notifications || [];
      if (append) {
        setFeedNotifications(prev => [...prev, ...items]);
      } else {
        setFeedNotifications(items);
      }
      setFeedHasMore(items.length >= FEED_PAGE_SIZE);
      setFeedSkip(skip + items.length);
      if (countRes) setFeedUnreadCount(countRes.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notification feed:', error);
    } finally {
      setFeedLoading(false);
      setFeedLoadingMore(false);
    }
  }, [apiCall, feedUnreadOnly, FEED_PAGE_SIZE]);

  useEffect(() => {
    fetchFeed(0);
  }, [feedUnreadOnly]); // refetch when filter changes

  const handleMarkOneRead = async (notifId) => {
    try {
      await apiCall(`/api/admin/notifications/${notifId}/read`, { method: 'POST' });
      setFeedNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setFeedUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const handleMarkAllFeedRead = async () => {
    try {
      await apiCall('/api/admin/notifications/mark-all-read', { method: 'POST' });
      setFeedNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setFeedUnreadCount(0);
    } catch { /* ignore */ }
  };

  const getNotifColor = (type) => {
    const colors = {
      new_vendor: 'bg-blue-500',
      listing_approval: 'bg-amber-500',
      vendor_status_change: 'bg-green-500',
      high_value_order: 'bg-purple-500',
      system_alert: 'bg-red-500',
      lead: 'bg-blue-500',
      order: 'bg-green-500',
      announcement: 'bg-indigo-500',
    };
    return colors[type] || 'bg-gray-400';
  };

  const getNotifIcon = (type) => {
    const icons = {
      new_vendor: Store,
      listing_approval: AlertCircle,
      vendor_status_change: CheckCircle2,
      high_value_order: CreditCard,
      system_alert: AlertCircle,
      lead: Users,
      order: ShoppingCart,
      announcement: Megaphone,
    };
    const Icon = icons[type] || Bell;
    return <Icon className="h-4 w-4" />;
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    toast.success('Setting updated');
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      // Save settings to backend
      await apiCall('/api/admin/notification-settings', {
        method: 'POST',
        body: JSON.stringify(settings)
      });
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const searchVendors = async (query) => {
    if (!query || query.length < 2) { setVendorResults([]); return; }
    setVendorSearching(true);
    try {
      const data = await apiCall(`/api/vendors/?search=${encodeURIComponent(query)}`);
      const vendors = Array.isArray(data) ? data : (data?.vendors || data?.items || []);
      setVendorResults(vendors.map(v => ({ id: v.id, name: v.name || v.email, email: v.email })));
    } catch { setVendorResults([]); }
    finally { setVendorSearching(false); }
  };

  const toggleVendorSelection = (vendor) => {
    setPushNotification(prev => {
      const ids = prev.vendor_ids || [];
      const exists = ids.includes(vendor.id);
      return { ...prev, vendor_ids: exists ? ids.filter(id => id !== vendor.id) : [...ids, vendor.id] };
    });
  };

  const handleSendNotification = async () => {
    if (!pushNotification.title || !pushNotification.message) {
      toast.error('Please fill in title and message');
      return;
    }

    setSending(true);
    try {
      const result = await apiCall('/api/admin/send-notification', {
        method: 'POST',
        body: JSON.stringify(pushNotification)
      });
      
      toast.success(`Notification sent to ${result.recipients || 0} recipients`);
      
      // Refresh history
      fetchHistory();

      // Reset form
      setPushNotification({
        title: '',
        message: '',
        target_audience: 'all_vendors',
        priority: 'normal',
        send_email: true,
        send_sms: false,
        send_push: true,
        schedule_type: 'now',
        scheduled_time: ''
      });
    } catch (error) {
      const msg = String(error?.message || error || '');
      console.error('[AdminNotifications] Send failed:', msg);
      if (msg.includes('Session expired') || msg.includes('401') || msg.includes('unauthor')) {
        toast.error('Your session has expired. Please log in again.');
      } else if (msg.includes('403') || msg.includes('forbidden')) {
        toast.error("You don't have permission to send notifications.");
      } else if (msg.includes('Failed to fetch') || msg.includes('network') || msg.includes('Network')) {
        toast.error('Network error — check your connection and retry.');
      } else {
        toast.error(`Failed to send notification: ${msg || 'Unknown error'}`);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 md:pb-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Notifications</h1>
          <p className="text-gray-500 mt-1">Manage your notification preferences and send announcements</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="all" className="flex items-center gap-2" data-testid="all-notifications-tab">
              <Inbox className="h-4 w-4" />
              <span className="hidden sm:inline">All Notifications</span>
              <span className="sm:hidden">All</span>
              {feedUnreadCount > 0 && (
                <span className="ml-1 h-5 min-w-[20px] px-1 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                  {feedUnreadCount > 99 ? '99+' : feedUnreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
              <span className="sm:hidden">Settings</span>
            </TabsTrigger>
            <TabsTrigger value="push" className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              <span className="hidden sm:inline">Send</span>
              <span className="sm:hidden">Send</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">History</span>
              <span className="sm:hidden">History</span>
            </TabsTrigger>
          </TabsList>

          {/* All Notifications Tab */}
          <TabsContent value="all" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Inbox className="h-5 w-5" />
                      Notifications
                      {feedUnreadCount > 0 && (
                        <Badge variant="destructive" className="ml-1">{feedUnreadCount} unread</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>All admin notifications and alerts</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={feedUnreadOnly ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFeedUnreadOnly(prev => !prev)}
                      data-testid="filter-unread-btn"
                    >
                      <Filter className="h-3.5 w-3.5 mr-1.5" />
                      {feedUnreadOnly ? 'Unread Only' : 'All'}
                    </Button>
                    {feedUnreadCount > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleMarkAllFeedRead}
                        data-testid="mark-all-read-btn"
                      >
                        <CheckCheck className="h-3.5 w-3.5 mr-1.5" />
                        Mark All Read
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fetchFeed(0)}
                      disabled={feedLoading}
                      data-testid="refresh-feed-btn"
                    >
                      {feedLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {feedLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : feedNotifications.length === 0 ? (
                  <div className="text-center py-16" data-testid="empty-notifications">
                    <Bell className="h-14 w-14 mx-auto mb-4 text-gray-200" />
                    <p className="text-gray-500 font-medium">
                      {feedUnreadOnly ? 'No unread notifications' : 'No notifications yet'}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      {feedUnreadOnly
                        ? 'Switch to "All" to see read notifications'
                        : 'Notifications about vendors, listings, and system alerts will appear here'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1" data-testid="notifications-feed-list">
                    {feedNotifications.map((notif) => (
                      <div
                        key={notif.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          notif.read
                            ? 'bg-white border-gray-100 hover:bg-gray-50'
                            : 'bg-blue-50/60 border-blue-100 hover:bg-blue-50'
                        }`}
                        onClick={() => !notif.read && handleMarkOneRead(notif.id)}
                        data-testid={`notification-item-${notif.id}`}
                      >
                        {/* Type indicator dot + icon */}
                        <div className={`mt-0.5 flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white ${getNotifColor(notif.type)}`}>
                          {getNotifIcon(notif.type)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm ${notif.read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>
                              {notif.title}
                            </p>
                            <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                              {formatTimeAgo(notif.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 capitalize">
                              {(notif.type || 'general').replace(/_/g, ' ')}
                            </Badge>
                            {notif.priority === 'urgent' && (
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">Urgent</Badge>
                            )}
                            {notif.priority === 'high' && (
                              <Badge className="text-[10px] px-1.5 py-0 h-4 bg-orange-100 text-orange-700 border-orange-200">High</Badge>
                            )}
                          </div>
                        </div>

                        {/* Unread indicator */}
                        {!notif.read && (
                          <div className="flex-shrink-0 mt-2">
                            <div className="h-2.5 w-2.5 rounded-full bg-blue-500" title="Unread" />
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Load More */}
                    {feedHasMore && (
                      <div className="pt-4 text-center">
                        <Button
                          variant="outline"
                          onClick={() => fetchFeed(feedSkip, true)}
                          disabled={feedLoadingMore}
                          data-testid="load-more-btn"
                        >
                          {feedLoadingMore ? (
                            <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading...</>
                          ) : (
                            <><ChevronDown className="h-4 w-4 mr-2" /> Load More</>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Admin Alert Preferences */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Alert Preferences
                </CardTitle>
                <CardDescription>Choose which events trigger notifications for you</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">New Vendor Applications</p>
                    <p className="text-sm text-gray-500">Get notified when new vendors apply</p>
                  </div>
                  <Switch
                    checked={settings.new_vendor_applications}
                    onCheckedChange={(checked) => handleSettingChange('new_vendor_applications', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Listing Approval Requests</p>
                    <p className="text-sm text-gray-500">Notify when listings need approval</p>
                  </div>
                  <Switch
                    checked={settings.listing_approvals}
                    onCheckedChange={(checked) => handleSettingChange('listing_approvals', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Compliance Upgrade Requests</p>
                    <p className="text-sm text-gray-500">Notify when vendors request tier upgrades</p>
                  </div>
                  <Switch
                    checked={settings.compliance_requests}
                    onCheckedChange={(checked) => handleSettingChange('compliance_requests', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">High-Value Orders</p>
                    <p className="text-sm text-gray-500">Alert for orders above threshold</p>
                  </div>
                  <Switch
                    checked={settings.high_value_orders}
                    onCheckedChange={(checked) => handleSettingChange('high_value_orders', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Support Tickets</p>
                    <p className="text-sm text-gray-500">New support requests from vendors</p>
                  </div>
                  <Switch
                    checked={settings.support_tickets}
                    onCheckedChange={(checked) => handleSettingChange('support_tickets', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">System Alerts</p>
                    <p className="text-sm text-gray-500">Critical system notifications</p>
                  </div>
                  <Switch
                    checked={settings.system_alerts}
                    onCheckedChange={(checked) => handleSettingChange('system_alerts', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Delivery Channels */}
            <Card>
              <CardHeader>
                <CardTitle>Delivery Channels</CardTitle>
                <CardDescription>How you want to receive notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-sm text-gray-500">Receive notifications via email</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.email_enabled}
                    onCheckedChange={(checked) => handleSettingChange('email_enabled', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="font-medium">SMS</p>
                      <p className="text-sm text-gray-500">Receive notifications via SMS</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.sms_enabled}
                    onCheckedChange={(checked) => handleSettingChange('sms_enabled', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg bg-purple-50">
                  <div className="flex items-center gap-3">
                    <BellRing className="h-5 w-5 text-purple-500" />
                    <div>
                      <p className="font-medium">Browser Push Notifications</p>
                      <p className="text-sm text-gray-500">
                        {pushSupported 
                          ? (pushSubscribed ? 'Subscribed - you will receive browser alerts' : 'Click to enable browser notifications')
                          : 'Not supported in this browser'}
                      </p>
                    </div>
                  </div>
                  {pushLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
                  ) : (
                    <Switch
                      checked={pushSubscribed}
                      onCheckedChange={handleTogglePush}
                      disabled={!pushSupported}
                    />
                  )}
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Smartphone className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="font-medium">In-App Notifications</p>
                      <p className="text-sm text-gray-500">Notification bell in the dashboard</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.push_enabled}
                    onCheckedChange={(checked) => handleSettingChange('push_enabled', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Daily Digest */}
            <Card>
              <CardHeader>
                <CardTitle>Daily Digest</CardTitle>
                <CardDescription>Receive a summary of activities</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Enable Daily Digest</p>
                    <p className="text-sm text-gray-500">Get a daily summary email</p>
                  </div>
                  <Switch
                    checked={settings.daily_digest}
                    onCheckedChange={(checked) => handleSettingChange('daily_digest', checked)}
                  />
                </div>
                
                {settings.daily_digest && (
                  <div className="space-y-2">
                    <Label>Delivery Time</Label>
                    <Select 
                      value={settings.digest_time} 
                      onValueChange={(value) => handleSettingChange('digest_time', value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="06:00">6:00 AM</SelectItem>
                        <SelectItem value="09:00">9:00 AM</SelectItem>
                        <SelectItem value="12:00">12:00 PM</SelectItem>
                        <SelectItem value="18:00">6:00 PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            <Button onClick={handleSaveSettings} disabled={loading} className="w-full sm:w-auto">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Settings
            </Button>
          </TabsContent>

          {/* Push Notification Tab */}
          <TabsContent value="push" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Send Announcement
                </CardTitle>
                <CardDescription>Send notifications to vendors and customers</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Target Audience */}
                <div className="space-y-2">
                  <Label>Target Audience *</Label>
                  <Select 
                    value={pushNotification.target_audience}
                    onValueChange={(value) => setPushNotification(prev => ({ ...prev, target_audience: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_vendors">
                        <div className="flex items-center gap-2">
                          <Store className="h-4 w-4" />
                          All Vendors
                        </div>
                      </SelectItem>
                      <SelectItem value="all_customers">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          All Customers
                        </div>
                      </SelectItem>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Everyone
                        </div>
                      </SelectItem>
                      <SelectItem value="tier_1_vendors">Tier 1 Vendors</SelectItem>
                      <SelectItem value="tier_2_vendors">Tier 2 Vendors</SelectItem>
                      <SelectItem value="tier_3_vendors">Tier 3+ Vendors</SelectItem>
                      <SelectItem value="specific_vendors">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Specific Vendors (Handpick)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Vendor picker for specific vendors */}
                  {pushNotification.target_audience === 'specific_vendors' && (
                    <div className="mt-3 space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search vendors by name or email..."
                          value={vendorSearch}
                          onChange={(e) => { setVendorSearch(e.target.value); searchVendors(e.target.value); }}
                          className="pl-10"
                          data-testid="vendor-search-input"
                        />
                      </div>
                      {/* Search results */}
                      {vendorResults.length > 0 && (
                        <div className="border rounded-lg max-h-40 overflow-y-auto">
                          {vendorResults.map(v => (
                            <button
                              key={v.id}
                              onClick={() => toggleVendorSelection(v)}
                              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-gray-50 ${(pushNotification.vendor_ids || []).includes(v.id) ? 'bg-blue-50' : ''}`}
                            >
                              <span>{v.name} <span className="text-gray-400 text-xs">{v.email}</span></span>
                              {(pushNotification.vendor_ids || []).includes(v.id) && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Selected count */}
                      {(pushNotification.vendor_ids || []).length > 0 && (
                        <p className="text-xs text-blue-600 font-medium">{pushNotification.vendor_ids.length} vendor(s) selected</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Notification Title *</Label>
                  <Input
                    id="title"
                    value={pushNotification.title}
                    onChange={(e) => setPushNotification(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Important Platform Update"
                    maxLength={100}
                  />
                  <p className="text-xs text-gray-500">{pushNotification.title.length}/100 characters</p>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={pushNotification.message}
                    onChange={(e) => setPushNotification(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Enter your notification message..."
                    rows={4}
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-500">{pushNotification.message.length}/500 characters</p>
                </div>

                {/* Priority */}
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={pushNotification.priority}
                    onValueChange={(value) => setPushNotification(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - General info</SelectItem>
                      <SelectItem value="normal">Normal - Standard announcement</SelectItem>
                      <SelectItem value="high">High - Important update</SelectItem>
                      <SelectItem value="urgent">Urgent - Requires immediate attention</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Delivery Channels */}
                <div className="space-y-3">
                  <Label>Delivery Channels</Label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={pushNotification.send_email}
                        onCheckedChange={(checked) => setPushNotification(prev => ({ ...prev, send_email: checked }))}
                      />
                      <Mail className="h-4 w-4 text-blue-500" />
                      <span>Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={pushNotification.send_sms}
                        onCheckedChange={(checked) => setPushNotification(prev => ({ ...prev, send_sms: checked }))}
                      />
                      <MessageSquare className="h-4 w-4 text-green-500" />
                      <span>SMS</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={pushNotification.send_push}
                        onCheckedChange={(checked) => setPushNotification(prev => ({ ...prev, send_push: checked }))}
                      />
                      <Bell className="h-4 w-4 text-purple-500" />
                      <span>In-App</span>
                    </label>
                  </div>
                </div>

                {/* Schedule */}
                <div className="space-y-3">
                  <Label>When to Send</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="schedule"
                        checked={pushNotification.schedule_type === 'now'}
                        onChange={() => setPushNotification(prev => ({ ...prev, schedule_type: 'now' }))}
                        className="w-4 h-4"
                      />
                      <span>Send Now</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="schedule"
                        checked={pushNotification.schedule_type === 'scheduled'}
                        onChange={() => setPushNotification(prev => ({ ...prev, schedule_type: 'scheduled' }))}
                        className="w-4 h-4"
                      />
                      <span>Schedule</span>
                    </label>
                  </div>
                  
                  {pushNotification.schedule_type === 'scheduled' && (
                    <Input
                      type="datetime-local"
                      value={pushNotification.scheduled_time}
                      onChange={(e) => setPushNotification(prev => ({ ...prev, scheduled_time: e.target.value }))}
                      className="max-w-xs"
                    />
                  )}
                </div>

                {/* Preview */}
                {(pushNotification.title || pushNotification.message) && (
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <p className="text-sm text-gray-500 mb-2">Preview</p>
                    <div className="bg-white p-3 rounded-lg shadow-sm border">
                      <p className="font-semibold">{pushNotification.title || 'Notification Title'}</p>
                      <p className="text-sm text-gray-600 mt-1">{pushNotification.message || 'Your message will appear here...'}</p>
                    </div>
                  </div>
                )}

                {/* Send Button */}
                <Button 
                  onClick={handleSendNotification} 
                  disabled={sending || !pushNotification.title || !pushNotification.message}
                  className="w-full"
                  size="lg"
                >
                  {sending ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Send Notification</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <History className="h-5 w-5" />
                      Notification History
                    </CardTitle>
                    <CardDescription>Previously sent announcements and their status</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchHistory} disabled={historyLoading}>
                    {historyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No notifications sent yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history.map((item) => (
                      <div key={item.id} className="p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium truncate">{item.title}</h4>
                              <Badge variant={item.status === 'delivered' || item.status === 'sent' ? 'default' : 'secondary'}>
                                {item.status === 'delivered' || item.status === 'sent' ? (
                                  <><CheckCircle2 className="h-3 w-3 mr-1" /> {item.status}</>
                                ) : (
                                  <><Clock className="h-3 w-3 mr-1" /> {item.status}</>
                                )}
                              </Badge>
                              {item.priority === 'urgent' && (
                                <Badge variant="destructive">Urgent</Badge>
                              )}
                              {item.priority === 'high' && (
                                <Badge variant="warning" className="bg-orange-100 text-orange-700">High</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-2">{item.message}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {item.target_audience === 'all_vendors' ? 'All Vendors' :
                                 item.target_audience === 'all_customers' ? 'All Customers' :
                                 item.target_audience === 'all' ? 'Everyone' :
                                 item.target_audience || item.target}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(item.sent_at).toLocaleString()}
                              </span>
                              {(item.recipients_count > 0 || item.recipients > 0) && (
                                <span>{item.recipients_count || item.recipients} recipients</span>
                              )}
                              {item.send_email && <Badge variant="outline" className="text-xs">Email</Badge>}
                              {item.send_push && <Badge variant="outline" className="text-xs">Push</Badge>}
                              {item.send_sms && <Badge variant="outline" className="text-xs">SMS</Badge>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminNotifications;
